import { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONDITION_OPTIONS = [
	{ value: 'is', label: 'is' },
	{ value: 'is_not', label: 'is not' },
	{ value: 'contains', label: 'contains' },
	{ value: 'does_not_contain', label: 'does not contain' }
];

const PageFilterModal = ({
	isOpen,
	onClose,
	onApply,
	websiteId,
	token,
	analytics
}) => {
	const [filters, setFilters] = useState({
		page: { condition: CONDITION_OPTIONS[0], values: [] },
		entry_page: { condition: CONDITION_OPTIONS[0], values: [] },
		exit_page: { condition: CONDITION_OPTIONS[0], values: [] }
	});
	// `options` holds the full searchable path list. We seed it from the dashboard's
	// top/entry/exit page slices (instant render) and replace it with the full
	// distinct path list from /available-paths when that response arrives.
	const [options, setOptions] = useState([]);

	// Auth header — token is null in publicMode (PublicAnalyticsPage). The
	// /available-paths endpoint accepts unauthenticated reads on is_public sites.
	const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

	/* ------------------------------------
	   1. Seed options from analytics for instant feedback (no network wait)
	   2. Replace with the full distinct path list from the backend
	   ------------------------------------ */
	useEffect(() => {
		if (!isOpen) return;

		// 1) Seed: merge top_pages + entry_pages + exit_pages so the dropdown
		//    shows *something* immediately, even before /available-paths returns.
		if (analytics) {
			const seedSet = new Set();
			(analytics.top_pages || []).forEach(p => p?.path && seedSet.add(p.path));
			(analytics.entry_pages || []).forEach(p => p?.path && seedSet.add(p.path));
			(analytics.exit_pages || []).forEach(p => p?.path && seedSet.add(p.path));
			setOptions(
				Array.from(seedSet)
					.sort()
					.map(path => ({ label: path, value: path }))
			);
		}

		// 2) Fetch the FULL distinct path list so search covers every page
		//    that's ever been tracked, not just the top-10 currently on screen.
		if (!websiteId) return;
		const abort = new AbortController();
		(async () => {
			try {
				const res = await axios.get(
					`${API}/analytics/${websiteId}/available-paths`,
					{ headers: authHeaders, signal: abort.signal }
				);
				const paths = (res.data?.paths || []).filter(Boolean);
				setOptions(paths.map(path => ({ label: path, value: path })));
			} catch (err) {
				if (axios.isCancel(err)) return;
				// Non-fatal: the seeded top/entry/exit pages still let the user pick.
				console.error('PageFilterModal: failed to load available paths', err);
			}
		})();
		return () => abort.abort();
	}, [isOpen, analytics, websiteId, token]);

	const updateFilter = (type, key, value) => {
		setFilters(prev => ({
			...prev,
			[type]: { ...prev[type], [key]: value }
		}));
	};

	const handleApply = () => {
		const fParams = [];

		Object.entries(filters).forEach(([type, filter]) => {
			if (!filter.values || filter.values.length === 0) return;

			const joinedValues = filter.values
				.map(v => v.value)
				.join('|');

			fParams.push(
				`${filter.condition.value},${type},${joinedValues}`
			);
		});

		/*
		  Example output:
		  [
			"is,page,/Boulder/|/",
			"is,exit_page,/hollywood/schedule/"
		  ]
		*/
		onApply(fParams);
		onClose();
	};




	if (!isOpen) return null;

	const FilterRow = ({ label, type, hint }) => (
		<div style={{ marginBottom: '18px' }}>
			<div style={labelStyle}>{label}</div>
			{hint && <div style={hintStyle}>{hint}</div>}

			<div style={{ display: 'flex', gap: '8px' }}>
				<div style={{ width: '140px' }}>
					<Select
						options={CONDITION_OPTIONS}
						value={filters[type].condition}
						onChange={val => updateFilter(type, 'condition', val)}
						isSearchable={false}
						styles={selectStyles}
					/>
				</div>

				<div style={{ flex: 1 }}>
					{/* Shared path option set across all three rows. react-select's
					    built-in typeahead does the searching — no extra wiring needed.
					    closeMenuOnSelect=false lets the user pick several pages in one
					    open of the menu, matching Plausible's filter UX. */}
					<Select
						options={options}
						value={filters[type].values}
						onChange={val => updateFilter(type, 'values', val || [])}
						isMulti
						isClearable
						isSearchable
						closeMenuOnSelect={false}
						placeholder={`Select ${label}`}
						noOptionsMessage={() => 'No matching pages'}
						styles={selectStyles}
					/>
				</div>
			</div>
		</div>
	);

	return (
		<div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
			<div style={modalStyle}>
				{/* Header */}
				<div style={headerStyle}>
					<h3 style={titleStyle}>
						Filter by Page
					</h3>
					<button
						onClick={onClose}
						style={closeBtnStyle}
						onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
						onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
					>
						<X size={16} />
					</button>
				</div>

				{/* Body */}
				<FilterRow
					label="Page"
					type="page"
					hint="Narrows Top Pages and pageview counts to the chosen URL(s)."
				/>
				<FilterRow
					label="Entry page"
					type="entry_page"
					hint="Keeps sessions whose FIRST pageview matched. Other pages those visitors browsed still appear in Top Pages."
				/>
				<FilterRow
					label="Exit page"
					type="exit_page"
					hint="Keeps sessions whose LAST pageview matched. Other pages those visitors browsed still appear in Top Pages."
				/>

				{/* Footer */}
				<div style={footerStyle}>
					<Button
						onClick={handleApply}
						style={{ backgroundColor: '#6366f1', color: '#fff' }}
					>
						Apply filter
					</Button>
				</div>
			</div>
		</div>
	);
};

/* ---------- Styles ---------- */

const selectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 40,
		fontSize: 14,
		borderRadius: 12,
		borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
		boxShadow: state.isFocused
			? '0 0 0 3px rgba(99, 102, 241, 0.12)'
			: '0 1px 2px rgba(15, 23, 42, 0.04)',
		'&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
		transition: 'box-shadow 0.2s, border-color 0.2s'
	}),
	placeholder: (base) => ({
		...base,
		color: '#94a3b8'
	}),
	singleValue: (base) => ({
		...base,
		color: '#0f172a'
	}),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: (base) => ({
		...base,
		color: '#94a3b8',
		padding: 6
	}),
	menu: (base) => ({
		...base,
		borderRadius: 12,
		boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
		border: '1px solid #e2e8f0',
		overflow: 'hidden'
	}),
	option: (base, state) => ({
		...base,
		fontSize: 13,
		color: '#0f172a',
		backgroundColor: state.isSelected
			? '#eef2ff'
			: state.isFocused
				? '#f8fafc'
				: 'transparent',
		cursor: 'pointer'
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: '#eef2ff',
		borderRadius: 9999,
		paddingLeft: 4,
		paddingRight: 2
	}),
	multiValueLabel: (base) => ({
		...base,
		color: '#4338ca',
		fontSize: 12,
		fontWeight: 500
	}),
	multiValueRemove: (base) => ({
		...base,
		color: '#4338ca',
		borderRadius: 9999,
		':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
	})
};

const overlayStyle = {
	position: 'fixed',
	inset: 0,
	backgroundColor: 'rgba(15, 23, 42, 0.45)',
	backdropFilter: 'blur(2px)',
	WebkitBackdropFilter: 'blur(2px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 9999
};

const modalStyle = {
	width: '460px',
	background: '#fff',
	borderRadius: '16px',
	padding: '24px',
	boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: '20px'
};

const titleStyle = {
	margin: 0,
	fontSize: '16px',
	fontWeight: 600,
	color: '#0f172a',
	letterSpacing: '-0.01em'
};

const labelStyle = {
	fontSize: '12px',
	fontWeight: 500,
	color: '#64748b',
	marginBottom: '8px'
};

const hintStyle = {
	fontSize: '11px',
	color: '#94a3b8',
	lineHeight: 1.4,
	marginTop: '-4px',
	marginBottom: '8px'
};

const footerStyle = {
	marginTop: '20px',
	paddingTop: '16px',
	borderTop: '1px solid #f1f5f9',
	display: 'flex',
	justifyContent: 'flex-end'
};

const closeBtnStyle = {
	background: 'transparent',
	border: 'none',
	cursor: 'pointer',
	color: '#64748b',
	width: 28,
	height: 28,
	borderRadius: 9999,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'background-color 0.15s, color 0.15s'
};

export default PageFilterModal;
